import type { TourOutcome } from 'definitions';
import type { RunningTour } from './tour-types';
import type { Box, BubblePosition } from './tour-position';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { cn } from 'helpers/cn';
import { findAnchor } from './anchors';
import { placeBubble, spotlightRingPath } from './tour-position';
import { useTourTrigger } from './use-tour-trigger';
import { useTours } from './use-tours';

/**
 * The product tour, drawn over the whole shell. Mounted once in DashboardLayout: it starts
 * tours when they are due and draws the one that is running.
 */
export const TourLayer = () => {
  useTourTrigger();
  const { running, setRunning, markSeen } = useTours();

  if (!running) return null;

  const finish = (outcome: TourOutcome) => {
    setRunning(null);
    if (!running.isReplay) markSeen({ tourKey: running.tour.key, version: running.version, outcome });
  };

  // Keyed so a new tour starts at its first step.
  return createPortal(
    <RunningTourView key={`${running.tour.key}:${running.isReplay}`} running={running} onFinish={finish} />,
    document.body,
  );
};

type RunningTourViewProps = { running: RunningTour; onFinish: (outcome: TourOutcome) => void };

const SPOT_PAD = 6;
const SPOT_RADIUS = 10;
const SPOT_RING = 2;
/** How long the bubble and the spotlight glide from one step's element to the next. */
const GLIDE_MS = 320;

const sameBox = (a: Box | null, b: Box | null) =>
  !!a && !!b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;

const RunningTourView = ({ running, onFinish }: RunningTourViewProps) => {
  const { steps, isUpdate } = running;
  const [index, setIndex] = useState(0);
  const [anchorBox, setAnchorBox] = useState<Box | null>(null);
  const [position, setPosition] = useState<BubblePosition | null>(null);
  // Only a change of step glides. The first placement fades in where it lands instead of
  // sliding in from the corner, and following a scroll tracks the element frame by frame
  // rather than trailing behind it.
  const [isGliding, setIsGliding] = useState(false);
  const hasShownStep = useRef(false);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const nextRef = useRef<HTMLButtonElement | null>(null);

  const step = steps[index];
  const isLast = index === steps.length - 1;

  const next = () => (isLast ? onFinish('completed') : setIndex(index + 1));
  const back = () => setIndex(Math.max(0, index - 1));

  // Follows the element every frame, so the bubble keeps up with scrolling, resizing and
  // content loading above it. Only re-renders when the element actually moved.
  useEffect(() => {
    if (!step) return;
    findAnchor(step.anchor)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });

    let glideTimer: ReturnType<typeof setTimeout> | null = null;
    if (hasShownStep.current) {
      setIsGliding(true);
      glideTimer = setTimeout(() => setIsGliding(false), GLIDE_MS);
    }
    hasShownStep.current = true;

    let frame = 0;
    let last: Box | null = null;
    const follow = () => {
      const element = findAnchor(step.anchor);
      const rect = element?.getBoundingClientRect();
      const box =
        rect && rect.width > 0 ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height } : null;

      if (!box) {
        // The element went away mid-tour (a panel closed, data changed). Move on rather
        // than point at nothing.
        next();
        return;
      }

      const bubble = bubbleRef.current;
      if (!sameBox(box, last) && bubble) {
        last = box;
        setAnchorBox(box);
        setPosition(
          placeBubble({
            anchor: box,
            bubble: { width: bubble.offsetWidth, height: bubble.offsetHeight },
            viewport: { width: window.innerWidth, height: window.innerHeight },
            preferred: step.placement,
          }),
        );
      }
      frame = requestAnimationFrame(follow);
    };

    frame = requestAnimationFrame(follow);
    nextRef.current?.focus();
    return () => {
      cancelAnimationFrame(frame);
      if (glideTimer) clearTimeout(glideTimer);
    };
  }, [index]);

  useEffect(() => {
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        onFinish('skipped');
      } else if (ev.key === 'ArrowRight') next();
      else if (ev.key === 'ArrowLeft') back();
      else if (ev.key === 'Tab') {
        // The bubble is modal: Tab cycles its own buttons instead of walking the page behind.
        const buttons = Array.from(bubbleRef.current?.querySelectorAll<HTMLElement>('button') ?? []);
        if (!buttons.length) return;
        ev.preventDefault();
        const at = buttons.indexOf(document.activeElement as HTMLElement);
        const direction = ev.shiftKey ? -1 : 1;
        buttons[(at + direction + buttons.length) % buttons.length]?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [index]);

  if (!step) return null;

  return (
    <div className="fixed inset-0 z-[60]">
      {/* Catches every click outside the bubble: a tour cannot be lost by accident, and
          the page under it cannot be changed halfway through. */}
      <div className="absolute inset-0" aria-hidden="true" />

      {anchorBox && (
        <div
          aria-hidden="true"
          className={cn(
            'tour-spotlight pointer-events-none fixed rounded-[10px]',
            isGliding && 'transition-all duration-300 ease-out motion-reduce:transition-none',
          )}
          style={{
            top: anchorBox.top - SPOT_PAD,
            left: anchorBox.left - SPOT_PAD,
            width: anchorBox.width + SPOT_PAD * 2,
            height: anchorBox.height + SPOT_PAD * 2,
            boxShadow: '0 0 0 9999px rgb(0 0 0 / 0.5)',
          }}
        >
          <span
            className="tour-spot-ring"
            style={{
              clipPath: spotlightRingPath({
                width: anchorBox.width + SPOT_PAD * 2,
                height: anchorBox.height + SPOT_PAD * 2,
                radius: SPOT_RADIUS,
                thickness: SPOT_RING,
              }),
            }}
          />
        </div>
      )}

      <div
        ref={bubbleRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-title"
        aria-describedby="tour-body"
        data-tour-bubble=""
        className={cn(
          'tour-bubble fixed flex w-[300px] max-w-[calc(100vw-16px)] flex-col gap-2 rounded-xl px-4 pb-3 pt-3.5 shadow-xl',
          isGliding && 'transition-[top,left] duration-300 ease-out motion-reduce:transition-none',
          position ? 'tour-bubble-in' : 'invisible',
        )}
        style={{ top: position?.top ?? 0, left: position?.left ?? 0 }}
      >
        {/* In this order: the fill covers the ring but for its outer edge. */}
        <span className="tour-glow tour-glow-halo" aria-hidden="true" />
        <span className="tour-glow tour-glow-ring" aria-hidden="true" />
        <span className="tour-glow tour-glow-fill" aria-hidden="true" />

        <div className="flex items-center justify-between gap-2">
          <span className="micro-label">
            Step {index + 1} of {steps.length}
          </span>
          {isUpdate && (
            <span className="bg-accent rounded px-1.5 py-px font-mono text-nano font-medium uppercase tracking-[0.1em] text-white">
              New
            </span>
          )}
        </div>
        <h2 id="tour-title" className="text-foreground m-0 text-body font-medium">
          {step.title}
        </h2>
        <p id="tour-body" className="supporting m-0">
          {step.body}
        </p>

        <div className="mt-1 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => onFinish('skipped')}
            className="text-dim hover:text-foreground text-meta hover:underline"
          >
            Skip tour
          </button>
          <div className="flex gap-1.5">
            {index > 0 && (
              <Button size="sm" variant="outline" onClick={back} className="h-8 rounded-lg">
                Back
              </Button>
            )}
            <Button ref={nextRef} size="sm" onClick={next} className="h-8 rounded-lg">
              {isLast ? 'Done' : 'Next'}
            </Button>
          </div>
        </div>

        {steps.length > 1 && (
          <div className="flex gap-1" aria-hidden="true">
            {steps.map((item, dot) => (
              <span
                key={item.anchor}
                className={cn('size-1.5 rounded-full', dot === index ? 'bg-accent' : 'bg-line')}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
